import React, { createContext, useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { adminService, authService, mfaService } from "@/services";
import { clearVault } from "@/lib/session-vault";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isApprover: boolean;
  isAdminOrApprover: boolean;
  /**
   * Non-null when the role read itself FAILED (network/RLS/permission error) —
   * distinct from "authenticated but holds no privileged role". Guards must
   * render a retryable error for this, never an Access Denied or a bounce.
   */
  roleError: string | null;
  adminTenantId: string | null;
  setAdminTenantId: (id: string) => void;
  mfaRequired: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signInWithMagicLink: (email: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  completeMfaChallenge: () => void;
  /**
   * Force a re-read of the caller's roles from the database and wait until
   * the AuthContext state has been updated. Returns the freshly resolved
   * `isAdmin` snapshot so callers can gate navigation on it. Used after
   * flows that grant a role in the same request the user is already
   * authenticated for (e.g. invite redemption) — the pre-grant
   * onAuthStateChange run would otherwise leave a stale `isAdmin=false`
   * cached in context and race the redirect to a role-gated page.
   */
  refreshRoles: () => Promise<{ isAdmin: boolean; isApprover: boolean; isSuperAdmin: boolean; tenantId: string | null }>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  loading: true,
  isAdmin: false,
  isSuperAdmin: false,
  isApprover: false,
  isAdminOrApprover: false,
  roleError: null,
  adminTenantId: null,
  setAdminTenantId: () => {},
  mfaRequired: false,
  signIn: async () => ({ error: null }),
  signInWithMagicLink: async () => ({ error: null }),
  signUp: async () => ({ error: null }),
  signOut: async () => {},
  completeMfaChallenge: () => {},
  refreshRoles: async () => ({ isAdmin: false, isApprover: false, isSuperAdmin: false, tenantId: null }),
});


export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  const isAdminOrApprover = isAdmin || isApprover;
  const [roleError, setRoleError] = useState<string | null>(null);
  const [adminTenantId, setAdminTenantIdState] = useState<string | null>(() => {
    return localStorage.getItem("cardmoji_admin_tenant_id");
  });
  const [mfaRequired, setMfaRequired] = useState(false);

  const setAdminTenantId = (id: string | null) => {
    setAdminTenantIdState(id);
    if (id) {
      localStorage.setItem("cardmoji_admin_tenant_id", id);
    } else {
      localStorage.removeItem("cardmoji_admin_tenant_id");
    }
  };

  const checkAdminRole = async (userId: string) => {
    // Role hydration must never reject into an un-caught promise chain: that
    // skipped setLoading(false) and left the app on a permanent spinner, and a
    // partial read presented as isAdmin=false ("Access Denied") rather than a
    // retryable failure.
    let data: { role: string; tenant_id: string }[] | null = null;
    try {
      const res = await adminService.checkUserRoles(userId);
      if (res.error) throw new Error(res.error as any);
      data = (res.data as any) ?? null;
      setRoleError(null);
    } catch (e: any) {
      setRoleError(e?.message || "Could not verify your permissions.");
      return;
    }

    if (data && data.length > 0) {
      const isSuper = data.some((r: { role: string }) => r.role === "super_admin");
      const hasAdmin = data.some((r: { role: string }) => r.role === "admin" || r.role === "super_admin");
      const hasApprover = data.some((r: { role: string }) => r.role === "approver");
      setIsAdmin(hasAdmin);
      setIsSuperAdmin(isSuper);
      setIsApprover(hasApprover);

      if (isSuper) {
        // super_admin: any tenant is fine; only snap if completely unset.
        if (!adminTenantId) setAdminTenantId(data[0].tenant_id);
      } else {
        // Reconcile against the full accessible list (direct + cascaded descendants),
        // so a group admin who picked a child tenant keeps that selection.
        const { supabase } = await import("@/integrations/supabase/client");
        const { data: accessible } = await supabase.rpc("list_accessible_tenants_for_user", { _user_id: userId });
        const accessibleIds = (accessible ?? []).map((t: any) => t.id);
        const directIds = data.map((r: { tenant_id: string }) => r.tenant_id);
        const fallback = accessibleIds[0] ?? directIds[0];
        if (!adminTenantId || (!accessibleIds.includes(adminTenantId) && !directIds.includes(adminTenantId))) {
          setAdminTenantId(fallback);
        }
      }
    } else {
      // Ship A: do NOT null adminTenantId on a transient zero-row read.
      // The invite flow fires this between verifyOtp and redeem — clearing
      // the tenant here races with refreshRoles' later restore and can leave
      // context stuck at null even though the row now exists. Role flags are
      // authoritative for gating; a leftover tenantId with isAdmin=false is
      // never reachable through ProtectedRoute.
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setIsApprover(false);
    }
  };

  const checkMfa = async () => {
    const { data } = await mfaService.getAssuranceLevel();
    if (data) {
      // If user has enrolled TOTP factors but hasn't verified at AAL2 yet
      if (data.currentLevel === "aal1" && data.nextLevel === "aal2") {
        setMfaRequired(true);
        return;
      }
    }
    setMfaRequired(false);
  };

  useEffect(() => {
    let cancelled = false;
    // The initial hydration is authoritative: `loading` must stay true until
    // BOTH the stored session AND the caller's roles have resolved. Otherwise
    // ProtectedRoute decides on a half-hydrated context (user=null → bounce to
    // /login, or isSuperAdmin=false → Access Denied) and the unified login
    // sends the user straight back into the same race.
    let initialDone = false;

    // Set up auth state listener FIRST
    const { data: { subscription } } = authService.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          Sentry.setUser({ id: session.user.id, email: session.user.email });
          const uid = session.user.id;
          // Deferred (never awaited inside the callback — Supabase forbids
          // awaiting its own client here), but `loading` is only released
          // once roles have actually landed.
          setTimeout(async () => {
            try {
              await checkAdminRole(uid);
              await checkMfa();
            } catch (e: any) {
              if (!cancelled) setRoleError(e?.message || "Could not verify your permissions.");
            } finally {
              if (!cancelled) {
                initialDone = true;
                setLoading(false);
              }
            }
          }, 0);
        } else {
          Sentry.setUser(null);
          setIsAdmin(false);
          setIsSuperAdmin(false);
          setIsApprover(false);
          setAdminTenantId(null);
          setMfaRequired(false);
          // A null session from the listener is only trustworthy once the
          // stored-session read has completed; before that it is just the
          // pre-hydration tick and must not unlock the guards.
          if (initialDone) setLoading(false);
        }
      }
    );

    // THEN get initial session. Everything here is wrapped: a throw inside the
    // chain used to skip setLoading(false) entirely → permanent spinner.
    (async () => {
      try {
        const { data: { session } } = await authService.getSession();
        if (cancelled) return;
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          Sentry.setUser({ id: session.user.id, email: session.user.email });
          await checkAdminRole(session.user.id);
          await checkMfa();
        }
      } catch (e: any) {
        if (!cancelled) setRoleError(e?.message || "Could not restore your session.");
      } finally {
        if (!cancelled) {
          initialDone = true;
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);


  const signIn = async (email: string, password: string) => {
    const { error } = await authService.signInWithPassword(email, password);
    if (!error) {
      // After sign in, check if MFA is needed
      await checkMfa();
    }
    return { error };
  };

  const signInWithMagicLink = async (email: string) => {
    const { error } = await authService.signInWithMagicLink(email);
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await authService.signUp(email, password);
    return { error };
  };

  const signOut = async () => {
    await authService.signOut();
    clearVault();
    Sentry.setUser(null);
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setIsSuperAdmin(false);
    setIsApprover(false);
    setAdminTenantId(null);
    setMfaRequired(false);
    setRoleError(null);
    localStorage.removeItem("cardmoji_last_route");
  };

  const completeMfaChallenge = () => {
    setMfaRequired(false);
  };

  /**
   * Force a re-read from the DB and await state application. Callers gate
   * navigation on the returned snapshot rather than the (still-stale)
   * context values in the current render. Same underlying query as the
   * onAuthStateChange path, so no divergence in what "has admin" means.
   */
  const refreshRoles = async () => {
    const uid = user?.id ?? (await authService.getSession()).data.session?.user?.id;
    if (!uid) {
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setIsApprover(false);
      return { isAdmin: false, isApprover: false, isSuperAdmin: false, tenantId: null };
    }
    let rows: { role: string; tenant_id: string }[] = [];
    try {
      const res = await adminService.checkUserRoles(uid);
      if (res.error) throw new Error(res.error as any);
      rows = ((res.data as any) ?? []) as { role: string; tenant_id: string }[];
      setRoleError(null);
    } catch (e: any) {
      setRoleError(e?.message || "Could not verify your permissions.");
      return { isAdmin, isApprover, isSuperAdmin, tenantId: adminTenantId };
    }
    const isSuper = rows.some((r: { role: string }) => r.role === "super_admin");
    const hasAdmin = rows.some((r: { role: string }) => r.role === "admin" || r.role === "super_admin");
    const hasApprover = rows.some((r: { role: string }) => r.role === "approver");
    setIsAdmin(hasAdmin);
    setIsSuperAdmin(isSuper);
    setIsApprover(hasApprover);

    // Ship B: return the resolved tenantId so callers (e.g. the invite
    // redemption flow) can setAdminTenantId explicitly before navigating.
    // Relying on this function's own setState to have flushed by the time
    // the destination mounts is racy — the pillbox mounted with null once
    // and never saw a change event because the value already matched.
    let resolvedTenantId: string | null = adminTenantId;

    if (rows.length > 0) {
      if (isSuper) {
        if (!adminTenantId) {
          resolvedTenantId = rows[0].tenant_id;
          setAdminTenantId(resolvedTenantId);
        }
      } else {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data: accessible } = await supabase.rpc("list_accessible_tenants_for_user", { _user_id: uid });
        const accessibleIds = (accessible ?? []).map((t: any) => t.id);
        const directIds = rows.map((r: { tenant_id: string }) => r.tenant_id);
        const fallback = accessibleIds[0] ?? directIds[0] ?? null;
        if (!adminTenantId || (!accessibleIds.includes(adminTenantId) && !directIds.includes(adminTenantId))) {
          if (fallback) {
            resolvedTenantId = fallback;
            setAdminTenantId(fallback);
          }
        }
      }
    } else {
      resolvedTenantId = null;
      setAdminTenantId(null);
    }

    return { isAdmin: hasAdmin, isApprover: hasApprover, isSuperAdmin: isSuper, tenantId: resolvedTenantId };
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, isSuperAdmin, isApprover, isAdminOrApprover, roleError, adminTenantId, setAdminTenantId, mfaRequired, signIn, signInWithMagicLink, signUp, signOut, completeMfaChallenge, refreshRoles }}>
      {children}
    </AuthContext.Provider>
  );
};

